import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  HELP_BODY_MAX,
  HELP_SUBJECT_MAX,
  HELP_TYPES,
  HELP_TYPE_LABELS,
  type HelpType,
} from '@ready/shared';
import { useApi } from '@/lib/api';
import { useCurrentOrg } from '@/lib/current-org';
import { Button, ErrorText, Field, Input, Select, Textarea } from '@/components/ui';
import { Modal } from '../modal';
import { AttachmentPicker, useAttachments } from './attachments';

/**
 * "Support": a question or request to the Nirvik team (the default) or one
 * of the user's organizations, with up to three screenshots or PDFs. Opens
 * the request's page once sent.
 */
export function HelpDialog({ onClose }: { onClose: () => void }) {
  const api = useApi();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { orgs } = useCurrentOrg();
  const { pathname } = useLocation();
  const [to, setTo] = useState('');
  const [type, setType] = useState<HelpType>('question');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const attachments = useAttachments();
  const send = useMutation({
    mutationFn: () =>
      api.createHelpRequest({
        orgId: to || null,
        type,
        subject: subject.trim(),
        body: body.trim(),
        pageUrl: pathname,
        fileIds: attachments.ids,
      }),
    onSuccess: ({ id }) => {
      queryClient.invalidateQueries({ queryKey: ['myHelp'] });
      onClose();
      navigate(`/help/${id}`);
    },
  });
  const ready = subject.trim().length >= 3 && body.trim().length > 0 && !attachments.busy;

  return (
    <Modal title="Support" onClose={onClose}>
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (ready) send.mutate();
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          {orgs.length > 0 && (
            <Field label="Ask" htmlFor="help-to">
              <Select id="help-to" value={to} onChange={(e) => setTo(e.target.value)}>
                <option value="">The Nirvik Ready team</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
          )}
          <Field label="About" htmlFor="help-type">
            <Select
              id="help-type"
              value={type}
              onChange={(e) => setType(e.target.value as HelpType)}
            >
              {HELP_TYPES.map((t) => (
                <option key={t} value={t}>
                  {HELP_TYPE_LABELS[t]}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field label="Subject" htmlFor="help-subject">
          <Input
            id="help-subject"
            maxLength={HELP_SUBJECT_MAX}
            value={subject}
            placeholder="e.g. Can't open the final assessment"
            onChange={(e) => setSubject(e.target.value)}
          />
        </Field>
        <Field
          label="Details"
          htmlFor="help-body"
          hint={`${body.length.toLocaleString('en-US')} / ${HELP_BODY_MAX.toLocaleString('en-US')}`}
        >
          <Textarea
            id="help-body"
            rows={5}
            maxLength={HELP_BODY_MAX}
            value={body}
            placeholder="What were you doing, what happened, and what did you expect?"
            onChange={(e) => setBody(e.target.value)}
          />
        </Field>
        <AttachmentPicker state={attachments} />
        <p className="text-xs text-muted-foreground">
          We&rsquo;ll email you when someone replies. Sent along with the page you&rsquo;re on:{' '}
          <span className="font-mono">{pathname}</span>
        </p>
        <ErrorText error={send.error} />
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={!ready || send.isPending}>
            {send.isPending ? 'Sending…' : 'Send request'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
